import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, runJob } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Loader2, Sparkles, ArrowLeft, Wand2, Plus, X } from "lucide-react";
import { Field, Section } from "@/components/national/Fields";
import { PreviewShell, EditPanel } from "@/components/national/NationalPreview";

const HOURS = "Open 24 hours, 7 days a week";
const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 60);

function SubpageEditor({ id, sid }) {
  const [sub, setSub] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [frameKey, setFrameKey] = useState(0);
  const load = useCallback(() => api.get(`/national/${id}/subpages/${sid}`).then(({ data }) => setSub(data)).catch(() => {}), [id, sid]);
  useEffect(() => { load(); }, [load]);
  const busy = sub && (["deploying", "deleting"].includes(sub.status) || sub.gen_status === "running");
  useEffect(() => {
    if (!busy) return undefined;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [busy, load]);
  if (!sub?.has_draft) return null;
  return (
    <div className="space-y-5" data-testid="subpage-editor">
      {busy && <div className={`${ui.card} p-3 text-sm text-sky-300 flex items-center gap-2`} data-testid="subpage-busy"><Loader2 className="w-4 h-4 spin" /> {sub.gen_status === "running" ? "Generating…" : "Publishing & verifying the live page…"}</div>}
      {sub.last_error && <div className={`${ui.card} p-3 text-sm text-rose-300`}>{sub.last_error}</div>}
      <PreviewShell url={sub.preview_url} viewing={viewing} onBack={() => setViewing(null)} frameKey={frameKey} />
      <EditPanel basePath={`/national/${id}/subpages/${sid}`} live={Boolean(sub.is_published)} disabled={busy} testid="subpage"
        placeholder={'e.g. "Make the headline mention same-day AC repair and add an FAQ about weekend appointments"'}
        hint="Describe a change in plain English — headline, intro, about text, service descriptions, FAQs, SEO title or logo. To change services or the address, use the form below."
        onView={setViewing} onApplied={() => { setViewing(null); setFrameKey((k) => k + 1); load(); }} />
    </div>
  );
}

function AreaPicker({ areas, setAreas, city, state }) {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const add = () => { const x = v.trim(); if (x && !areas.includes(x)) setAreas([...areas, x]); setV(""); };
  const suggest = async () => {
    if (!city || !state) return toast.error("Enter City and State first");
    setBusy(true);
    try { const { data } = await api.post("/service-area-suggest", { city, state }); setAreas(Array.from(new Set([...areas, ...data.areas]))); toast.success(`Suggested ${data.areas.length} areas — edit as needed`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`${ui.card} p-6`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={ui.sectionHeader}>Service Area</h2>
        <button data-testid="sub-ai-suggest-areas" onClick={suggest} disabled={busy} className={ui.btnGhost}>{busy ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4 text-indigo-400" />} AI Suggest</button>
      </div>
      <div className="flex gap-2 mb-3">
        <input data-testid="sub-areas-input" className={ui.input} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add a neighborhood or city…" />
        <button data-testid="sub-areas-add" onClick={add} className={ui.btnGhost}><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="sub-area-chips">
        {areas.map((a) => <span key={a} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-200">{a}<button onClick={() => setAreas(areas.filter((y) => y !== a))} className="text-slate-500 hover:text-rose-400"><X className="w-3.5 h-3.5" /></button></span>)}
        {!areas.length && <span className="text-sm text-slate-600">No areas yet — add manually or use AI Suggest (defaults to the city).</span>}
      </div>
    </div>
  );
}

export default function SubpageForm() {
  const { id, sid } = useParams();
  const navigate = useNavigate();
  const [nat, setNat] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [b, setB] = useState({ name: "", phone: "", address: "", city: "", state: "", zip: "", logo_url: "" });
  const [orig, setOrig] = useState({});
  const [areas, setAreas] = useState([]);
  const [selected, setSelected] = useState([]);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const profile = profiles.find((p) => p.id === profileId);
  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get(`/national/${id}`).then(({ data }) => setNat(data)).catch((e) => toast.error(apiErr(e)));
    api.get("/profiles").then(({ data: profs }) => {
      setProfiles(profs);
      if (!sid) { if (profs[0]) { setProfileId(profs[0].id); setSelected(profs[0].services.map((s) => s.name)); } return; }
      api.get(`/national/${id}/subpages/${sid}`).then(({ data }) => {
        const x = data.business;
        const prof = profs.find((p) => p.id === x.category_profile_id) || profs.find((p) => p.category_name === x.category) || profs[0];
        setOrig(x); setProfileId(prof?.id || "");
        setSelected(x.services.map((s) => s.name));
        setB({ name: x.name, phone: x.phone, address: x.address?.street || "", city: x.address?.city || "", state: x.address?.state || "", zip: x.address?.zip || "", logo_url: x.logo_url || "" });
        setAreas(x.service_areas || []); setSlug(data.slug); setSlugTouched(true);
      }).catch((e) => toast.error(apiErr(e)));
    }).catch(() => toast.error("Failed to load categories"));
  }, [id, sid]);

  const pickProfile = (pid) => { setProfileId(pid); const p = profiles.find((x) => x.id === pid); setSelected(p ? p.services.map((s) => s.name) : []); };
  const toggle = (n) => setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const pageSlug = slugTouched ? slug : slugify(b.name);
  const domain = nat?.custom_domain || nat?.intended_domain || "yourdomain.com";

  const genLogo = async () => {
    if (!b.name || !profile) return toast.error("Enter a business name and pick a category first");
    setLogoBusy(true);
    try { const data = await runJob("/logo/preview", { business_name: b.name, category: profile.category_name }); setLogo(data); toast.success("Logo ready — it'll be applied to this page"); }
    catch (e) { toast.error(apiErr(e)); } finally { setLogoBusy(false); }
  };

  const submit = async () => {
    if (!profileId) return toast.error("Pick a category");
    if (!b.name || !b.phone || !b.address || !b.city || !b.state || !b.zip) return toast.error("Fill in all required business fields");
    if (!selected.length) return toast.error("Select at least one service");
    const body = {
      ...orig, name: b.name, phone: b.phone, category_profile_id: profileId, category: profile.category_name,
      services: selected.map((name) => ({ name, description: (orig.services || []).find((s) => s.name === name)?.description || "" })),
      service_areas: areas, address: { street: b.address, city: b.city, state: b.state, zip: b.zip },
      location_type: "storefront", show_address: true, show_map: true, hours: HOURS,
      logo_url: logo?.logo_url || b.logo_url || "", slug: pageSlug,
    };
    setSaving(true);
    try {
      if (sid) { await api.put(`/national/${id}/subpages/${sid}`, body); toast.success("Saved. Regenerate the page to rewrite its content, then publish."); }
      else { await api.post(`/national/${id}/subpages`, body); toast.success("Generating the business page…"); }
      navigate(`/national/${id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const input = (k, ph) => <input data-testid={`sub-${k}`} className={ui.input} value={b[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} />;

  return (
    <div className="animate-fade-up max-w-4xl space-y-5" data-testid="subpage-form">
      <div>
        <Link to={`/national/${id}`} className="text-sm text-slate-400 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> {nat?.name || "Back"}</Link>
        <h1 className={`${ui.pageHeader} mt-2`}>{sid ? "Edit Business Subpage" : "Create Business Subpage"}</h1>
        <p className={`${ui.muted} mt-1`}>Same details as a regular website — the page is published under {domain}.</p>
      </div>
      {sid && <SubpageEditor id={id} sid={sid} />}
      <Section title="Business">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Field label="Category" required>
            <select data-testid="sub-category" className={ui.input} value={profileId} onChange={(e) => pickProfile(e.target.value)}>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.category_name}</option>)}
            </select></Field></div>
          <div className="sm:col-span-2"><Field label="Business Name" required>{input("name", "Hardy HVAC Solutions")}</Field></div>
          <Field label="Phone" required>{input("phone", "(425) 963-6246")}</Field>
          <Field label="ZIP" required>{input("zip", "98105")}</Field>
          <div className="sm:col-span-2"><Field label="Full Address" required>{input("address", "4546 University Wy NE")}</Field></div>
          <Field label="City" required>{input("city", "Seattle")}</Field>
          <Field label="State" required>{input("state", "WA")}</Field>
          <div className="sm:col-span-2"><Field label="Logo URL (optional)">{input("logo_url", "https://…")}</Field></div>
          <div className="sm:col-span-2"><Field label="Page URL" required hint={`https://${domain}/${pageSlug || "slug"}/`}>
            <input data-testid="sub-slug" className={ui.input} value={pageSlug} onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} /></Field></div>
        </div>
      </Section>
      <AreaPicker areas={areas} setAreas={setAreas} city={b.city} state={b.state} />
      <Section title="Services">
        <div className="grid sm:grid-cols-2 gap-2" data-testid="sub-services">
          {profile?.services.map((s) => (
            <label key={s.anchor} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-800/60 cursor-pointer text-sm text-slate-200">
              <input type="checkbox" data-testid={`sub-service-${s.anchor}`} checked={selected.includes(s.name)} onChange={() => toggle(s.name)} className="w-4 h-4 accent-indigo-500" />{s.name}
            </label>
          ))}
        </div>
      </Section>
      <Section title="Brand Logo">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">Auto-create a logo from the business name (~30–60s). Hours are always shown as open 24 hours.</p>
          <button type="button" data-testid="sub-generate-logo" onClick={genLogo} disabled={logoBusy} className={ui.btnGhost}>{logoBusy ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4 text-indigo-400" />} {logo ? "Regenerate" : "Generate Logo"}</button>
        </div>
        {(logo?.logo_url || b.logo_url) && <div className="rounded-lg bg-white border border-slate-800 p-5 flex items-center justify-center"><img data-testid="sub-logo-preview" src={logo?.logo_url || b.logo_url} alt="logo" style={{ maxHeight: 88, maxWidth: "100%" }} /></div>}
      </Section>
      <div className="flex justify-end">
        <button data-testid="sub-submit" onClick={submit} disabled={saving} className={ui.btnPrimary}>
          {saving ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4" />} {sid ? "Save Changes" : "Generate Business Page"}
        </button>
      </div>
    </div>
  );
}
